import {
  BackupService,
  backupCompanyName,
  backupDataForTarget,
  type BackupBundle,
} from '@infra/backup/BackupService';
import { exportBackupData } from '@infra/backup/BackupDataSource';
import type { NexxusDatabase } from '@infra/persistence/NexxusDatabase';
import { CoffreFortFileBridge } from '@infra/persistence/CoffreFortFileBridge';
import { logger } from '@infra/telemetry/Logger';
import {
  b64ToBytes,
  createEnvelope,
  HybridCryptoError,
  openEnvelope,
  type HybridEnvelope,
  type HybridUnlockSecret,
} from './HybridBackupCrypto';
import {
  defaultHybridBackupTransport,
  describeHybridTransportError,
  HybridTransportError,
  type HybridBackupMeta,
  type HybridBackupTransport,
} from './HybridBackupTransport';
import { ElectronSafetyBackupStore, type HybridSafetyBackupStore } from './HybridSafetyBackupStore';

export type HybridRestoreStep =
  | 'download'
  | 'verify_hash'
  | 'decrypt'
  | 'check_content'
  | 'safety_backup'
  | 'import'
  | 'verify_integrity'
  | 'restore_files'
  | 'done';

export type HybridRestoreErrorCode =
  | 'DOWNLOAD_FAILED'
  | 'RESTORE_WINDOW_CLOSED'
  | 'LICENSE_REVOKED'
  | 'SERVER_INTEGRITY_ERROR'
  | 'BACKUP_NOT_FOUND'
  | 'HASH_MISSING'
  | 'HASH_MISMATCH'
  | 'ENVELOPE_INVALID'
  | 'WRONG_SECRET'
  | 'RECOVERY_KEY_INVALID'
  | 'AUTH_FAILED'
  | 'UNSUPPORTED_FORMAT'
  | 'CONTENT_INVALID'
  | 'SAFETY_BACKUP_FAILED'
  | 'IMPORT_FAILED'
  | 'INTEGRITY_MISMATCH'
  | 'BACKUP_TENANT_MISMATCH';

export interface HybridRestoreReport {
  ok: boolean;
  backupId: string;
  backupCreatedAt: string | null;
  stepsDone: HybridRestoreStep[];
  /** Comptages par table annoncés par le coffre. */
  expectedCounts: Record<string, number>;
  /** Comptages réellement présents dans la base après import. */
  actualCounts: Record<string, number>;
  /** Lignes écrites par table. */
  restored: Record<string, number>;
  /** Sauvegarde de sécurité de la base existante (null si la base était vide). */
  safetyBackup: { location: string } | null;
  /** true si les données du coffre appartenaient à un autre dossier ET que le transfert a été explicitement autorisé. */
  tenantRemapped: boolean;
  files: { restored: number; failed: number };
  /** true tant qu'aucune écriture n'a eu lieu ou si l'écriture a été annulée (tout ou rien). */
  databaseUnchanged: boolean;
  errorCode?: HybridRestoreErrorCode;
  message?: string;
}

export interface HybridRestoreDeps {
  db: NexxusDatabase;
  tenantId: string;
  transport?: HybridBackupTransport;
  safetyStore?: HybridSafetyBackupStore;
  /** Restaure les pièces du coffre-fort (défaut : pont Electron s'il est disponible). */
  restoreFiles?: (tenantId: string, files: PlainFile[]) => Promise<{ restored: number; failed: number }>;
  onProgress?: (step: HybridRestoreStep) => void;
  now?: () => Date;
}

interface PlainFile {
  name: string;
  relative_path?: string;
  content_base64: string;
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new Uint8Array(bytes) as unknown as BufferSource);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

async function defaultRestoreFiles(
  tenantId: string,
  files: PlainFile[],
): Promise<{ restored: number; failed: number }> {
  let restored = 0;
  let failed = 0;
  if (!CoffreFortFileBridge.isAvailable()) return { restored, failed: files.length };
  for (const f of files) {
    try {
      const rel = f.relative_path ?? '';
      const cut = rel.indexOf('_');
      if (cut <= 0) throw new Error('chemin non reconstructible');
      const res = await CoffreFortFileBridge.write(
        tenantId,
        rel.slice(0, cut),
        rel.slice(cut + 1),
        b64ToBytes(f.content_base64).buffer as ArrayBuffer,
      );
      if (res.relativePath !== rel) throw new Error('chemin différent');
      restored += 1;
    } catch {
      failed += 1;
    }
  }
  return { restored, failed };
}

function isEmptyForSafety(counts: Record<string, number>): boolean {
  return Object.entries(counts).every(([table, n]) => table === 'tenants' || n === 0);
}

/** Restauration d'une sauvegarde chiffrée Hybride vers ce poste (poste neuf ou après incident). */
export class HybridRestoreService {
  /** Sauvegardes disponibles (métadonnées seulement), la plus récente d'abord. */
  static async listAvailable(
    transport: HybridBackupTransport = defaultHybridBackupTransport,
  ): Promise<HybridBackupMeta[]> {
    const items = await transport.list();
    return [...items].sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
  }

  static async restore(
    input: {
      backupId: string;
      secret: HybridUnlockSecret;
      /** Transfert d'entreprise explicite (jamais par défaut) : rattache les données au dossier courant. */
      allowTenantTransfer?: boolean;
    },
    deps: HybridRestoreDeps,
  ): Promise<HybridRestoreReport> {
    const transport = deps.transport ?? defaultHybridBackupTransport;
    const report: HybridRestoreReport = {
      ok: false,
      backupId: input.backupId,
      backupCreatedAt: null,
      stepsDone: [],
      expectedCounts: {},
      actualCounts: {},
      restored: {},
      safetyBackup: null,
      tenantRemapped: false,
      files: { restored: 0, failed: 0 },
      databaseUnchanged: true,
    };
    const step = (s: HybridRestoreStep): void => {
      report.stepsDone.push(s);
      deps.onProgress?.(s);
    };
    const fail = (code: HybridRestoreErrorCode, message: string): HybridRestoreReport => {
      report.errorCode = code;
      report.message = message;
      logger.warn({ event: 'hybrid_restore_failed', code });
      return report;
    };

    // (b) téléchargement + empreinte SHA-256
    let bytes: Uint8Array;
    let expectedHash: string | null;
    try {
      const dl = await transport.download(input.backupId);
      bytes = dl.bytes;
      expectedHash = dl.sha256;
    } catch (err) {
      const code: HybridRestoreErrorCode =
        err instanceof HybridTransportError
          ? err.serverCode === 'restore_window_closed'
            ? 'RESTORE_WINDOW_CLOSED'
            : err.serverCode === 'license_revoked'
              ? 'LICENSE_REVOKED'
              : err.serverCode === 'integrity_error'
                ? 'SERVER_INTEGRITY_ERROR'
                : err.code === 'NOT_FOUND'
                  ? 'BACKUP_NOT_FOUND'
                  : 'DOWNLOAD_FAILED'
          : 'DOWNLOAD_FAILED';
      return fail(code, describeHybridTransportError(err));
    }
    step('download');
    if (!expectedHash) return fail('HASH_MISSING', 'Le serveur n’a pas fourni l’empreinte SHA-256 : restauration refusée.');
    if ((await sha256Hex(bytes)) !== expectedHash.toLowerCase()) {
      return fail('HASH_MISMATCH', 'Empreinte SHA-256 différente : le fichier reçu est corrompu ou altéré.');
    }
    step('verify_hash');

    // (c) déchiffrement : phrase secrète OU clé de récupération
    let envelope: HybridEnvelope;
    try {
      envelope = JSON.parse(new TextDecoder().decode(bytes)) as HybridEnvelope;
    } catch {
      return fail('ENVELOPE_INVALID', 'Fichier de sauvegarde illisible.');
    }
    report.backupCreatedAt = typeof envelope?.created_at === 'string' ? envelope.created_at : null;
    let plaintext: string;
    let dek: Uint8Array;
    try {
      const opened = await openEnvelope(envelope, input.secret);
      plaintext = opened.plaintext;
      dek = opened.dek;
    } catch (err) {
      if (err instanceof HybridCryptoError) {
        const map: Partial<Record<HybridCryptoError['code'], HybridRestoreErrorCode>> = {
          WRONG_SECRET: 'WRONG_SECRET',
          RECOVERY_KEY_INVALID: 'RECOVERY_KEY_INVALID',
          AUTH_FAILED: 'AUTH_FAILED',
          UNSUPPORTED_FORMAT: 'UNSUPPORTED_FORMAT',
        };
        return fail(map[err.code] ?? 'ENVELOPE_INVALID', err.message);
      }
      return fail('ENVELOPE_INVALID', 'Déchiffrement impossible.');
    }
    step('decrypt');

    // contenu = exactement le format d'export .bkp ; vérification AVANT toute écriture
    const backup = new BackupService(deps.db);
    let bundle: BackupBundle;
    let files: PlainFile[] = [];
    try {
      const parsed = JSON.parse(plaintext) as BackupBundle & { coffre?: PlainFile[] };
      bundle = { manifest: parsed.manifest, data: parsed.data };
      if (Array.isArray(parsed.coffre)) files = parsed.coffre;
    } catch {
      return fail('CONTENT_INVALID', 'Contenu de la sauvegarde illisible.');
    }
    if (!(await backup.verify(bundle))) {
      return fail('CONTENT_INVALID', 'Contenu de la sauvegarde incohérent (intégrité interne en échec).');
    }
    report.expectedCounts = { ...bundle.manifest.counts };
    const otherTenant = bundle.manifest.tenant_id !== deps.tenantId;
    if (otherTenant && !input.allowTenantTransfer) {
      // P0-4 : jamais de réécriture silencieuse du dossier courant par une autre entreprise.
      const name = backupCompanyName(bundle);
      return fail(
        'BACKUP_TENANT_MISMATCH',
        `Cette sauvegarde appartient à une autre entreprise${name ? ` (« ${name} »)` : ''} : restauration refusée, rien n’a été modifié.`,
      );
    }
    report.tenantRemapped = otherTenant;
    step('check_content');

    // (d) sauvegarde de sécurité de l'existant, AVANT toute écriture
    const existing = await backup.export(deps.tenantId);
    if (!isEmptyForSafety(existing.manifest.counts)) {
      try {
        const store = deps.safetyStore ?? new ElectronSafetyBackupStore();
        const now = (deps.now?.() ?? new Date()).toISOString();
        const safety = await createEnvelope(JSON.stringify(existing), {
          dek,
          wrapping: { kdf: envelope.kdf, wraps: envelope.wraps },
          meta: { created_at: now, app_version: BackupService.APP_VERSION, schema_version: BackupService.SCHEMA_VERSION },
        });
        const name = `avant-restauration-${now.replace(/[:.]/g, '-')}.json`;
        report.safetyBackup = await store.write(name, JSON.stringify(safety));
      } catch {
        return fail(
          'SAFETY_BACKUP_FAILED',
          'Impossible de créer la sauvegarde de sécurité des données actuelles : restauration annulée, rien n’a été modifié.',
        );
      }
    }
    step('safety_backup');

    // (e) import atomique via le pipeline existant
    try {
      const res = await backup.import(bundle, {
        targetTenantId: deps.tenantId,
        atomic: true,
        allowTenantTransfer: input.allowTenantTransfer === true,
      });
      if (!res.integrity) return fail('CONTENT_INVALID', 'Intégrité refusée par le pipeline d’import.');
      report.restored = res.restored;
    } catch {
      return fail('IMPORT_FAILED', 'L’import a échoué : tout a été annulé, vos données actuelles sont intactes.');
    }
    report.databaseUnchanged = false;
    step('import');

    // (f) intégrité après import
    const expectedData =
      report.tenantRemapped ? backupDataForTarget(bundle, deps.tenantId) : bundle.data;
    const after = await exportBackupData(deps.db, deps.tenantId);
    report.actualCounts = { ...after.counts };
    const wasEmpty = isEmptyForSafety(existing.manifest.counts);
    for (const [table, expected] of Object.entries(report.expectedCounts)) {
      const rows = after.data[table] ?? [];
      const ids = new Set(rows.map((r) => (r as { id?: string }).id));
      const missing = (expectedData[table] ?? []).some((r) => !ids.has((r as { id?: string }).id));
      const tooFew = rows.length < expected;
      const notExact = wasEmpty && table !== 'tenants' && rows.length !== expected;
      if (missing || tooFew || notExact) {
        return fail(
          'INTEGRITY_MISMATCH',
          `Contrôle d’intégrité en échec pour « ${table} » après import. La sauvegarde de sécurité de vos anciennes données a été conservée.`,
        );
      }
    }
    step('verify_integrity');

    // pièces du coffre-fort (hors base : un échec est signalé, il n'annule pas les données)
    if (files.length > 0) {
      report.files = await (deps.restoreFiles ?? defaultRestoreFiles)(deps.tenantId, files);
    }
    step('restore_files');

    report.ok = true;
    step('done');
    logger.info({ event: 'hybrid_restore_ok', tables: Object.keys(report.restored).length });
    return report;
  }
}
